import React, { useEffect, useState } from 'react';
import { Plus, X, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const TYPES = ['LLC','Ltd','Inc','LLP','PLC','GmbH','SA','Holding','Foundation','Trust'];

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + submit effects. */
export default function Entities() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    legal_name:'', jurisdiction:'', entity_type:'LLC', registration_number:'', status:'active',
  });

  const load = async () => {
    const { data } = await api.get('/entities');
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/entities', form);
      toast.success('Entity created.');
      setOpen(false);
      setForm({ legal_name:'', jurisdiction:'', entity_type:'LLC', registration_number:'', status:'active' });
      load();
    } catch { toast.error('Failed to create'); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Entities & Subsidiaries</div>
          <h1 className="display text-3xl md:text-4xl mt-2">A centralised corporate record.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Manage subsidiaries across jurisdictions — always up to date, always audit-ready.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="entity-create" className="ve-btn-primary self-start"><Plus size={14}/> New entity</button>
      </div>

      <div className="mt-8 ve-card overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center">
            <Building2 size={28} className="mx-auto text-slate-300 mb-3"/>
            <h3 className="display text-xl">No entities yet</h3>
            <p className="text-sm text-slate-600 mt-2">Track every subsidiary, branch, and JV — never lose another corporate record.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.15em] text-xs">
              <tr>
                <th className="text-left px-5 py-3">Legal name</th>
                <th className="text-left px-5 py-3">Type</th>
                <th className="text-left px-5 py-3">Jurisdiction</th>
                <th className="text-left px-5 py-3">Reg #</th>
                <th className="text-left px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(e => (
                <tr key={e.entity_id} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium">{e.legal_name}</td>
                  <td className="px-5 py-3">{e.entity_type}</td>
                  <td className="px-5 py-3">{e.jurisdiction}</td>
                  <td className="px-5 py-3 mono text-xs">{e.registration_number || '—'}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded ${e.status==='active'?'bg-emerald-50 text-emerald-700':'bg-slate-100 text-slate-600'}`}>{e.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <form onSubmit={submit} className="ve-card p-7 w-full max-w-xl bg-white">
            <div className="flex items-center justify-between">
              <h2 className="display text-2xl">New entity</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="md:col-span-2 text-xs uppercase tracking-[0.18em] text-slate-500">Legal name
                <input required value={form.legal_name} onChange={e => setForm({...form, legal_name:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Type
                <select value={form.entity_type} onChange={e => setForm({...form, entity_type:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal">
                  {TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Jurisdiction
                <input required value={form.jurisdiction} onChange={e => setForm({...form, jurisdiction:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="md:col-span-2 text-xs uppercase tracking-[0.18em] text-slate-500">Registration #
                <input value={form.registration_number} onChange={e => setForm({...form, registration_number:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" className="ve-btn-primary">Create entity</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
